import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { 
  Building2, 
  Lock, 
  Mail, 
  Eye, 
  EyeOff, 
  Loader2,
  CheckCircle,
  AlertCircle,
  KeyRound
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { vendorApi } from '@/services/api';
import { useToast } from '@/hooks/use-toast';

export default function VendorAuthPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const token = searchParams.get('token');
  // Determine mode based on URL path AND token
  const isResetPasswordPath = location.pathname.includes('reset-password');
  const isActivatePath = location.pathname.includes('activate');
  const mode = token 
    ? (isResetPasswordPath ? 'reset' : 'activate') 
    : 'login';
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Validate token on mount
  useEffect(() => {
    if (token) {
      validateToken();
    }
  }, [token]);

  const validateToken = async () => {
    try {
      // For reset password, we validate the reset token differently
      if (mode === 'reset') {
        const result = await vendorApi.validateResetToken(token);
        setTokenValid(result.valid);
        if (result.valid) {
          setTokenInfo(result);
        } else {
          toast({ 
            title: 'Invalid Link', 
            description: result.reason || 'This password reset link is invalid or has expired',
            variant: 'destructive'
          });
        }
      } else {
        // Normal activation token validation
        const result = await vendorApi.validateToken(token);
        setTokenValid(result.valid);
        if (result.valid) {
          setTokenInfo(result);
        } else {
          toast({ 
            title: 'Invalid Invite', 
            description: result.reason || 'This invite link is invalid or expired',
            variant: 'destructive'
          });
        }
      }
    } catch (err) {
      setTokenValid(false);
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const result = await vendorApi.login(email, password);
      localStorage.setItem('vendor_token', result.token);
      localStorage.setItem('vendor_info', JSON.stringify(result.vendor));
      toast({ title: 'Welcome back!', description: `Logged in as ${result.vendor.company_name}` });
      navigate('/vendor/dashboard');
    } catch (err) {
      toast({ title: 'Login failed', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast({ title: 'Error', description: 'Passwords do not match', variant: 'destructive' });
      return;
    }
    
    if (password.length < 8) {
      toast({ title: 'Error', description: 'Password must be at least 8 characters', variant: 'destructive' });
      return;
    }
    
    setLoading(true);
    
    try {
      const result = await vendorApi.activate(token, password);
      localStorage.setItem('vendor_token', result.token);
      localStorage.setItem('vendor_info', JSON.stringify(result.vendor));
      toast({ title: 'Account activated!', description: `Welcome to GoCompareAI, ${result.vendor.contact_name}` });
      navigate('/vendor/dashboard');
    } catch (err) {
      toast({ title: 'Activation failed', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast({ title: 'Error', description: 'Passwords do not match', variant: 'destructive' });
      return;
    }
    
    if (password.length < 8) {
      toast({ title: 'Error', description: 'Password must be at least 8 characters', variant: 'destructive' });
      return;
    }
    
    setLoading(true);
    
    try {
      await vendorApi.resetPassword(token, password);
      toast({ title: 'Password reset!', description: 'You can now sign in with your new password' });
      navigate('/vendor');
    } catch (err) {
      toast({ title: 'Reset failed', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      toast({ title: 'Error', description: 'Please enter your email address', variant: 'destructive' });
      return;
    }
    
    try {
      await vendorApi.forgotPassword(email);
      toast({ title: 'Reset email sent', description: 'If the email exists, a password reset link has been sent' });
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  // Check if already logged in
  useEffect(() => {
    const vendorToken = localStorage.getItem('vendor_token');
    if (vendorToken && !token) {
      navigate('/vendor/dashboard');
    }
  }, []);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      
      <main className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              {mode === 'reset' ? (
                <KeyRound className="w-6 h-6 text-primary" />
              ) : (
                <Building2 className="w-6 h-6 text-primary" />
              )}
            </div>
            <CardTitle className="text-2xl">
              {mode === 'activate' ? 'Activate Your Account' : 
               mode === 'reset' ? 'Reset Your Password' : 
               'Vendor Portal'}
            </CardTitle>
            <CardDescription>
              {mode === 'activate' 
                ? 'Create your password to access your vendor dashboard'
                : mode === 'reset'
                ? 'Enter a new password for your account'
                : 'Sign in to manage your platform profile'
              }
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            {/* Invalid token state - for both activate and reset */}
            {(mode === 'activate' || mode === 'reset') && tokenValid === false && (
              <div className="flex flex-col items-center gap-4 py-6">
                <div className="w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-destructive" />
                </div>
                <p className="text-center text-muted-foreground">
                  {mode === 'reset' 
                    ? 'This password reset link is invalid or has expired. Please request a new one.'
                    : 'This invite link is invalid or has expired. Please contact GoCompareAI for a new invite.'
                  }
                </p>
                <Button variant="outline" onClick={() => navigate('/vendor')}>
                  Go to Login
                </Button>
              </div>
            )}
            
            {/* Reset Password Form */}
            {mode === 'reset' && tokenValid === true && (
              <form onSubmit={handleResetPassword} className="space-y-4">
                {tokenInfo && (
                  <div className="p-3 bg-muted rounded-lg text-center mb-4">
                    <p className="text-sm text-muted-foreground">Resetting password for</p>
                    <p className="font-medium">{tokenInfo.email || tokenInfo.company_name}</p>
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="password">New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-9 pr-10"
                      placeholder="Min. 8 characters"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="confirmPassword"
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-9"
                      placeholder="Confirm your password"
                      required
                    />
                  </div>
                </div>
                
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Reset Password
                </Button>
              </form>
            )}
            
            {/* Activate Account Form */}
            {mode === 'activate' && tokenValid === true && (
              <form onSubmit={handleActivate} className="space-y-4">
                {tokenInfo && (
                  <div className="p-3 bg-muted rounded-lg text-center mb-4">
                    <p className="text-sm text-muted-foreground">Creating account for</p>
                    <p className="font-medium">{tokenInfo.company_name}</p>
                    <p className="text-sm text-muted-foreground">{tokenInfo.vendor_name}</p>
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="password">Create Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-9 pr-10"
                      placeholder="Min. 8 characters"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="confirmPassword"
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-9"
                      placeholder="Confirm your password"
                      required
                    />
                  </div>
                </div>
                
                {/* Terms Acceptance */}
                <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg border">
                  <input
                    type="checkbox"
                    id="termsAccepted"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                    required
                  />
                  <label htmlFor="termsAccepted" className="text-sm text-muted-foreground">
                    I have read and agree to the{' '}
                    <a 
                      href="/vendors/terms" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline font-medium"
                    >
                      Vendor Partner Terms of Service
                    </a>
                    , including the editorial independence policy and tier features.
                  </label>
                </div>
                
                <Button type="submit" className="w-full" disabled={loading || !termsAccepted}>
                  {loading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Accept Terms & Activate Account
                </Button>
              </form>
            )}
            
            {mode === 'login' && (
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-9"
                      placeholder="your@email.com"
                      required
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-9 pr-10"
                      placeholder="Your password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Sign In
                </Button>
                
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  className="w-full text-sm text-muted-foreground hover:text-primary text-center"
                >
                  Forgot your password?
                </button>
              </form>
            )}
          </CardContent>
        </Card>
      </main>
      
      <Footer />
    </div>
  );
}
